import { ReactNode } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  SxProps,
  TableCell,
  TablePagination,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, Refresh, Search } from "@mui/icons-material";
import { FORM_GRID_SX, TIGHT_COL_SX } from "./dataTableStyles";

// Building blocks shared by the Ink, Generic and Curated database pages:
// one bordered card holding toolbar, table and pagination flush to its edges,
// so filters and rows are not inset twice and every page lines up the same way.

/** The card that holds a database table. Tables scroll sideways inside it, never the page. */
export function DataCard({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Paper sx={[{ overflow: "hidden" }, ...(Array.isArray(sx) ? sx : [sx])]}>{children}</Paper>
  );
}

/** Search + filters row at the top of a DataCard. Wraps onto a second line on narrow windows. */
export function DataToolbar({ children, summary }: { children: ReactNode; summary?: ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      {children}
      {summary !== undefined && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: "auto", whiteSpace: "nowrap" }}
        >
          {summary}
        </Typography>
      )}
    </Box>
  );
}

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Shows a small spinner at the end of the field while a search is in flight. */
  busy?: boolean;
}

export function SearchField({ value, onChange, placeholder, busy }: SearchFieldProps) {
  return (
    <TextField
      placeholder={placeholder}
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: busy ? (
          <InputAdornment position="end">
            <CircularProgress size={16} />
          </InputAdornment>
        ) : null,
      }}
      sx={{ flex: "1 1 260px", minWidth: 200, maxWidth: 480 }}
    />
  );
}

/** Edit + delete icons for a row, always on one line. */
export function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <TableCell align="right" sx={TIGHT_COL_SX}>
      <Tooltip title="Edit">
        <IconButton size="small" onClick={onEdit}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" color="error" onClick={onDelete}>
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    </TableCell>
  );
}

/** Header cell for the actions column, matching RowActions. */
export function ActionsHeadCell() {
  return (
    <TableCell align="right" sx={TIGHT_COL_SX}>
      Actions
    </TableCell>
  );
}

/** A single full-width row for loading and empty states, so the table header stays in place. */
export function TableMessageRow({
  colSpan,
  loading,
  children,
}: {
  colSpan: number;
  loading?: boolean;
  children?: ReactNode;
}) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 6, borderBottom: 0 }}>
        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {children}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

interface DataPaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

export function DataPagination({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: DataPaginationProps) {
  return (
    <TablePagination
      component="div"
      count={count}
      page={page}
      onPageChange={(_, newPage) => onPageChange(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={[10, 25, 50, 100]}
      sx={{ borderTop: 1, borderColor: "divider" }}
    />
  );
}

/** The page header's Refresh and "Add New" buttons. */
export function RefreshAndAdd({ onRefresh, onAdd }: { onRefresh: () => void; onAdd: () => void }) {
  return (
    <>
      <Tooltip title="Refresh">
        <IconButton onClick={onRefresh}>
          <Refresh />
        </IconButton>
      </Tooltip>
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={onAdd}
      >
        Add New
      </Button>
    </>
  );
}

/** Pagination under a table, shown once there is a row; a new page size goes back to page 1. */
export function TablePager({
  count,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
}: {
  count: number;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
}) {
  if (!(count > 0)) return null;
  return (
    <DataPagination
      count={count}
      page={page}
      onPageChange={setPage}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(n) => {
        setRowsPerPage(n);
        setPage(0);
      }}
    />
  );
}

interface EditRowDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSave: () => void;
  saveDisabled: boolean;
  saving: boolean;
  children: ReactNode;
}

/** The add / edit form dialog: fields in the form grid, Cancel and Save. */
export function EditRowDialog({ open, title, onClose, onSave, saveDisabled, saving, children }: EditRowDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {title}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={FORM_GRID_SX}>
          {children}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={saveDisabled}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** "Confirm Delete" with the row named in `children`. */
export function ConfirmDeleteDialog({ open, onCancel, onConfirm, deleting, children }: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  deleting: boolean;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>
          {children}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={deleting}
        >
          {deleting ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
